import { Fragment } from "react"
import { makeStyles } from "tss-react/mui"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material"
import { faBuilding, faUserTie, faPhone, faEnvelope } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { ResourceBookingCalendarHoveredEvent } from "./ResourceBookingCalendar"
import { BookingOrderDetailsItem } from "../../../createOrderTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        width: "16.25rem",
        boxShadow: "0px 2px 56px 0px rgba(0, 0, 0, 0.2)",
        padding: "1.25rem",
        background: theme.palette.common.white,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[950],
    },
    detailsWrap: {
        display: "grid",
        gridTemplateColumns: "1rem 1fr",
        gap: ".5rem",
        alignItems: "center",
    },
    accentColor: {
        color: theme.palette.text.primary,
    },
    greyColor: {
        color: theme.palette.tailwind.slate[500],
    },
    textPaddingRight: {
        paddingRight: ".5rem",
    },
    icon: {
        color: theme.palette.tailwind.slate[400],
    },

    skeleton: {
        background: theme.palette.tailwind.slate[100],
        borderRadius: ".75rem",
        transform: "none",
        height: "1.25rem",
    },
    divider: {
        borderColor: theme.palette.tailwind.slate[200],
    },
}))

type CalendarTooltipProps = Readonly<{
    details: BookingOrderDetailsItem
}>

export function CalendarTooltip({ details }: CalendarTooltipProps) {
    const { classes } = useStyles()
    const { id, meetingDetails, organizers = [] } = details

    return (
        <Stack className={classes.container} gap=".75rem">
            {meetingDetails && (
                <Stack gap=".25rem">
                    <Typography variant="subtitle1" className={classes.accentColor}>
                        <span className={classes.textPaddingRight}>{meetingDetails.meetingName}</span>
                        <span className={classes.greyColor}>#{id}</span>
                    </Typography>
                    <Typography variant="body2">{meetingDetails.meetingTimeAsText}</Typography>
                </Stack>
            )}
            {organizers.length > 0 && (
                <>
                    {organizers.map(({ id, name, company, mobilePhone, email }) => (
                        <Fragment key={id}>
                            <Divider className={classes.divider} />
                            {company && <DetailItem icon={faBuilding} text={company.name} />}
                            {name && <DetailItem icon={faUserTie} text={name} />}
                            {mobilePhone && <DetailItem icon={faPhone} text={mobilePhone} />}
                            {email && <DetailItem icon={faEnvelope} text={email} />}
                        </Fragment>
                    ))}
                </>
            )}
        </Stack>
    )
}

const DetailItem = ({ icon, text }: { icon: IconProp; text: string }) => {
    const { classes } = useStyles()
    return (
        <Box className={classes.detailsWrap}>
            <FontAwesomeIcon icon={icon} className={classes.icon} />
            <Typography variant="body2">{text}</Typography>
        </Box>
    )
}

export function CalendarTooltipSkeleton() {
    const { classes } = useStyles()

    return (
        <Stack className={classes.container} gap=".75rem">
            <Skeleton variant="rounded" height={48} className={classes.skeleton} />
            <Divider className={classes.divider} />
            <Skeleton variant="text" width="88%" className={classes.skeleton} />
            <Skeleton variant="text" width="60%" className={classes.skeleton} />
            <Skeleton variant="text" width="68%" className={classes.skeleton} />
            <Skeleton variant="text" width="88%" className={classes.skeleton} />
        </Stack>
    )
}

type CalendarLocalEventTooltipProps = Readonly<{
    start?: string
    end?: string
}>

export function CalendarLocalEventTooltip({ start, end }: CalendarLocalEventTooltipProps) {
    const { classes } = useStyles()

    return (
        <Stack className={classes.container} gap=".75rem">
            {start && (
                <LocalizedStrict id="create-order-hover-local-event-start" vars={{ start }}>
                    <Typography variant="body2">Start time: {start}</Typography>
                </LocalizedStrict>
            )}
            {end && (
                <LocalizedStrict id="create-order-hover-local-event-end" vars={{ end }}>
                    <Typography variant="body2">End time: {end}</Typography>
                </LocalizedStrict>
            )}
        </Stack>
    )
}

type CalendarOrderEventTooltipProps = Readonly<{
    hoveredEvent: ResourceBookingCalendarHoveredEvent
}>

export function CalendarOrderEventTooltip({ hoveredEvent }: CalendarOrderEventTooltipProps) {
    const { start, end, host, subject } = hoveredEvent
    const { classes } = useStyles()

    return (
        <Stack className={classes.container} gap=".75rem">
            {start && (
                <LocalizedStrict id="create-order-hover-local-event-start" vars={{ start }}>
                    <Typography variant="body2">Start time: {start}</Typography>
                </LocalizedStrict>
            )}
            {end && (
                <LocalizedStrict id="create-order-hover-local-event-end" vars={{ end }}>
                    <Typography variant="body2">End time: {end}</Typography>
                </LocalizedStrict>
            )}
            {host && (
                <LocalizedStrict id="create-order-hover-local-event-host" vars={{ host }}>
                    <Typography variant="body2">Host: {host}</Typography>
                </LocalizedStrict>
            )}
            {subject && <Typography variant="body2">subject</Typography>}
        </Stack>
    )
}
